import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { RestoreLocationsMap } from "@/components/site/RestoreLocationsMap";
import { scheduleUrl } from "@/lib/site-data";

import { LOCATION_IMAGES } from "@/assets/restore/locations";

const PHONE = "(866) 488-9356";

type City = { name: string; address: string; to: string | null };
type State = { name: string; slug: string; cities: City[] };

const STATES: State[] = [
  {
    name: "Arizona",
    slug: "arizona",
    cities: [
      { name: "Scottsdale", address: "7150 East Camelback Rd, Suite 444", to: "/locations/arizona/scottsdale/" },
    ],
  },
  {
    name: "California",
    slug: "california",
    cities: [
      { name: "Beverly Hills", address: "8383 Wilshire Blvd, Suite 800", to: "/locations/california/beverly-hills/" },
      { name: "Newport Beach", address: "895 Dove Street, 3rd Floor", to: "/locations/california/newport-beach/" },
      { name: "San Diego", address: "350 10th Ave, Suite 1000", to: "/locations/california/san-diego/" },
      { name: "San Francisco", address: "75 Broadway, Suite 202", to: "/locations/california/san-francisco/" },
    ],
  },
  {
    name: "Colorado",
    slug: "colorado",
    cities: [
      { name: "Boulder", address: "4770 Baseline Rd, Office 232", to: "/locations/colorado/boulder/" },
      { name: "Colorado Springs", address: "102 S. Tejon Street, Suite 1100", to: "/locations/colorado/colorado-springs/" },
      { name: "Downtown Denver", address: "2301 Blake St, Suite 113", to: "/locations/restore-denver/" },
      { name: "Greenwood Village", address: "5445 DTC Pkwy, Suite 1015", to: "/locations/restore-denver/" },
    ],
  },
  {
    name: "District of Columbia",
    slug: "district-of-columbia",
    cities: [
      { name: "Washington DC", address: "1875 I Street, NW, 5th Floor", to: "/locations/district-of-columbia/" },
    ],
  },
  {
    name: "Florida",
    slug: "florida",
    cities: [
      { name: "Boca Raton", address: "2255 Glades Road, Suite 324A", to: "/locations/florida/boca-raton/" },
      { name: "Jacksonville", address: "4651 Salisbury Rd, Suite 400", to: "/locations/florida/jacksonville/" },
      { name: "Miami", address: "80 SW 8th St, Suite 2000", to: "/locations/florida/miami/" },
      { name: "Tampa", address: "8875 Hidden River Pkwy, Suite 300", to: "/locations/florida/tampa/" },
    ],
  },
  {
    name: "Georgia",
    slug: "georgia",
    cities: [
      { name: "Atlanta", address: "1201 Peachtree Street, Suite 200", to: "/locations/georgia/atlanta/" },
    ],
  },
  {
    name: "Illinois",
    slug: "illinois",
    cities: [
      { name: "Chicago \u2013 Fulton Market", address: "159 N Sangamon St, Suite 200", to: "/locations/restore-chicago/" },
      { name: "Chicago \u2013 Hancock Center", address: "875 North Michigan Avenue, 31st Floor", to: "/locations/restore-chicago/" },
      { name: "Chicago Lincoln Park", address: "939 W. North Avenue, Suite 750", to: "/locations/restore-chicago/" },
      { name: "Deerfield", address: "500 Lake Cook Road, Suite #350", to: "/locations/restore-chicago/" },
      { name: "Lake Forest", address: "100 S. Saunders Rd, Suite 150", to: "/locations/restore-chicago/" },
      { name: "Oak Brook", address: "1415 22nd St, #950", to: "/locations/restore-chicago/" },
      { name: "Orland Park", address: "15255 S 94th Ave, 5th floor", to: "/locations/restore-chicago/" },
      { name: "Schaumburg", address: "10 N. Martingale Rd, Suite 400", to: "/locations/restore-chicago/" },
      { name: "St. Charles", address: "100 Illinois St, Suite 200", to: "/locations/restore-chicago/" },
    ],
  },
  {
    name: "Indiana",
    slug: "indiana",
    cities: [
      { name: "Indianapolis", address: "450 E. 96th St, Suite 500", to: "/locations/indiana/indianapolis/" },
    ],
  },
  {
    name: "Louisiana",
    slug: "louisiana",
    cities: [
      { name: "New Orleans", address: "1615 Poydras St, Suite 900", to: "/locations/louisiana/new-orleans/" },
    ],
  },
  {
    name: "Maryland",
    slug: "maryland",
    cities: [
      { name: "Baltimore", address: "4000 East Pratt St, 8th Floor", to: "/locations/maryland/baltimore/" },
    ],
  },
  {
    name: "Massachusetts",
    slug: "massachusetts",
    cities: [
      { name: "Boston", address: "75 Arlington Street, Suite 500", to: "/locations/massachusetts/boston/" },
    ],
  },
  {
    name: "Michigan",
    slug: "michigan",
    cities: [
      { name: "Novi", address: "39555 Orchard Hill Place, Suite 600", to: "/locations/michigan/novi/" },
    ],
  },
  {
    name: "Minnesota",
    slug: "minnesota",
    cities: [
      { name: "Edina", address: "5201 Eden Ave, Suite 300", to: "/locations/minnesota/edina/" },
    ],
  },
  {
    name: "Missouri",
    slug: "missouri",
    cities: [
      { name: "Kansas City", address: "2300 Main Street, Suite 900", to: "/locations/missouri/kansas-city/" },
      { name: "St. Louis", address: "2 Cityplace Dr, 2nd Floor", to: "/locations/missouri/st-louis/" },
    ],
  },
  {
    name: "Nevada",
    slug: "nevada",
    cities: [
      { name: "Henderson", address: "871 Coronado Center Drive, Suite 200", to: "/locations/nevada/las-vegas/" },
    ],
  },
  {
    name: "New Jersey",
    slug: "new-jersey",
    cities: [
      { name: "Neptune", address: "3600 Route 66", to: "/locations/new-jersey/neptune/" },
      { name: "Newark", address: "One Gateway Center", to: "/locations/new-jersey/newark/" },
      { name: "Paramus", address: "140 E Ridgewood Avenue", to: "/locations/new-jersey/paramus/" },
    ],
  },
  {
    name: "New York",
    slug: "new-york",
    cities: [
      { name: "Brooklyn Heights", address: "300 Cadman Plaza W", to: "/locations/new-york/brooklyn-heights/" },
      { name: "Manhasset", address: "1129 Northern Blvd", to: "/locations/restore-northeast/" },
      { name: "Manhattan", address: "240 W 37th St, 5th Floor", to: "/locations/restore-northeast/" },
      { name: "Melville", address: "555 Broadhollow Rd", to: "/locations/restore-northeast/" },
      { name: "Nassau County", address: "1979 Marcus Ave, Suite 210, Lake Success", to: "/locations/new-york/nassau-county/" },
      { name: "Queens", address: "118-35 Queens Blvd, Forest Hills Tower", to: "/locations/new-york/queens/" },
      { name: "White Plains", address: "50 Main Street, White Plains", to: "/locations/new-york/white-plains/" },
    ],
  },
  {
    name: "North Carolina",
    slug: "north-carolina",
    cities: [
      { name: "Charlotte", address: "7725 Ballantyne Commons Parkway, Suite 101", to: "/locations/restore-charlotte/" },
      { name: "Cornelius", address: "19109 West Catawba Ave, Suite 200", to: "/locations/restore-charlotte/" },
      { name: "Raleigh", address: "4509 Creedmoor Rd, Suite 201", to: "/locations/north-carolina/raleigh/" },
    ],
  },
  {
    name: "Ohio",
    slug: "ohio",
    cities: [
      { name: "Cincinnati", address: "300 E. Business Way, Suite 200", to: "/locations/ohio/cincinnati/" },
      { name: "Columbus", address: "100 E. Campus View Boulevard, Suite 250", to: "/locations/ohio/columbus/" },
      { name: "Independence", address: "6100 Oak Tree Boulevard, Suite 200", to: "/locations/ohio/independence/" },
    ],
  },
  {
    name: "Oklahoma",
    slug: "oklahoma",
    cities: [
      { name: "Oklahoma City", address: "101 Park Ave, Suite 1300", to: "/locations/oklahoma/oklahoma-city/" },
    ],
  },
  {
    name: "Oregon",
    slug: "oregon",
    cities: [
      { name: "Portland", address: "205 SE Spokane St, Suite 300", to: "/locations/oregon/portland/" },
    ],
  },
  {
    name: "Pennsylvania",
    slug: "pennsylvania",
    cities: [
      { name: "Philadelphia", address: "325-41 Chestnut Street, Suite 800", to: "/locations/pennsylvania/philadelphia/" },
      { name: "Pittsburgh", address: "322 North Shore Drive, Building 1B, Suite 200", to: "/locations/pennsylvania/pittsburgh/" },
    ],
  },
  {
    name: "Tennessee",
    slug: "tennessee",
    cities: [
      { name: "Nashville", address: "3200 West End Ave, Suite 500", to: "/locations/tennessee/nashville/" },
    ],
  },
  {
    name: "Texas",
    slug: "texas",
    cities: [
      { name: "Allen", address: "825 Watters Creek Blvd, Building M Suite 250", to: "/locations/texas/allen/" },
      { name: "Arlington", address: "3901 Arlington Highlands Blvd, Suite 200", to: "/locations/restore-dallas/" },
      { name: "Austin", address: "300 Beardsley Lane, Bldg C, Suite 101", to: "/locations/texas/austin/" },
      { name: "Dallas \u2013 Highland Park", address: "4514 Cole Avenue, Office #605", to: "/locations/texas/dallas-highland-park/" },
      { name: "Dallas \u2013 Plano", address: "5425 W Spring Creek, Suite 170", to: "/locations/restore-dallas/" },
      { name: "Fort Worth", address: "1751 River Run, Suite 200", to: "/locations/texas/fort-worth/" },
      { name: "Houston", address: "2929 Allen Pkwy, Suite 200", to: "/locations/texas/houston/" },
      { name: "San Antonio", address: "700 N St Mary's St, Suite 1400", to: "/locations/texas/san-antonio/" },
      { name: "South Lake", address: "950 E State Hwy 114", to: "/locations/texas/south-lake/" },
    ],
  },
  {
    name: "Utah",
    slug: "utah",
    cities: [
      { name: "Salt Lake City", address: "222 S Main St, 5th Floor", to: "/locations/utah/salt-lake-city/" },
    ],
  },
  {
    name: "Virginia",
    slug: "virginia",
    cities: [
      { name: "Virginia Beach", address: "780 Lynnhaven Pkwy, Suite 400", to: "/locations/virginia/virginia-beach/" },
    ],
  },
  {
    name: "Wisconsin",
    slug: "wisconsin",
    cities: [
      { name: "Milwaukee", address: "250 East Wisconsin Avenue, Suite 1800", to: "/locations/wisconsin/milwaukee/" },
    ],
  },
];

function mapUrl(address: string, city: string, state: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${address} ${city} ${state}`,
  )}`;
}

const NAME_IMAGE_OVERRIDES: Record<string, string> = {
  "Chicago \u2013 Fulton Market": "fulton-market",
  "Chicago \u2013 Hancock Center": "hancock",
  "Chicago Lincoln Park": "lincoln-park",
};

function cityImage(city: City): string | undefined {
  const key =
    NAME_IMAGE_OVERRIDES[city.name] ?? (city.to ? city.to.split("/").pop()! : "");
  return LOCATION_IMAGES[key];
}

export const Route = createFileRoute("/locations/")({
  head: () => ({
    meta: [
      { title: "Hair Transplant Locations Near You | RESTORE® Hair" },
      {
        name: "description",
        content:
          "Find a RESTORE® Hair transplant location near you. Over 50 clinics nationwide offering the No-Shave FUE™ procedure and free consultations.",
      },
      { property: "og:title", content: "Hair Transplant Locations Near You | RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "Browse RESTORE® Hair locations by state and book your free hair loss consultation at a clinic near you.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://restorehair.com/locations/" },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/locations/" }],
  }),
  component: LocationsIndex,
});

const REGIONS = [
  { label: "Charlotte", href: "/locations/restore-charlotte/" },
  { label: "Chicago", href: "/locations/restore-chicago/" },
  { label: "Dallas", href: "/locations/restore-dallas/" },
  { label: "Denver", href: "/locations/restore-denver/" },
  { label: "Northeast", href: "/locations/restore-northeast/" },
  { label: "Midwest", href: "/locations/restore-midwest/" },
  { label: "South", href: "/locations/restore-south/" },
  { label: "West", href: "/locations/restore-west/" },
];

function LocationsIndex() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="bg-background py-14 md:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
            <h1 className="text-3xl font-extrabold leading-tight text-foreground md:text-4xl">
              <span className="font-extrabold">RESTORE®</span>{" "}
              <span className="font-light">Hair Locations</span>
            </h1>
            {/* Every regional page, so each one has an internal link. */}
            <nav aria-label="Regions" className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm font-semibold">
              {REGIONS.map((r) => (
                <a key={r.href} href={r.href} className="text-foreground underline underline-offset-4 hover:text-primary">
                  {r.label}
                </a>
              ))}
            </nav>
            <nav className="mt-8 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
              {STATES.map((s) => (
                <a
                  key={s.slug}
                  href={`#${s.slug}`}
                  className="text-primary underline underline-offset-4 hover:no-underline"
                >
                  {s.name}
                </a>
              ))}
            </nav>
          </div>
        </section>

        <section className="bg-muted py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            {STATES.map((state) => (
              <div key={state.slug} id={state.slug} className="mb-12 scroll-mt-24 last:mb-0">
                <h2 className="border-b-2 border-primary pb-2 text-xl font-extrabold text-foreground md:text-2xl">
                  <Link
                    to={`/locations/${state.slug}/` as string}
                    className="transition-colors hover:text-primary"
                  >
                    {state.name}
                  </Link>
                </h2>
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {state.cities.map((city) => (
                    <article
                      key={city.name}
                      className="flex flex-col overflow-hidden border border-border bg-background shadow-sm"
                    >
                      {cityImage(city) ? (
                        city.to ? (
                          <Link to={city.to as string}>
                            <img
                              src={cityImage(city)}
                              alt={`RESTORE Hair clinic in ${city.name}, ${state.name}`}
                              loading="lazy"
                              className="h-48 w-full object-cover"
                            />
                          </Link>
                        ) : (
                          <img
                            src={cityImage(city)}
                            alt={`RESTORE Hair clinic in ${city.name}, ${state.name}`}
                            loading="lazy"
                            className="h-48 w-full object-cover"
                          />
                        )
                      ) : null}
                      <div className="flex flex-1 flex-col p-6">
                      <h3 className="text-lg font-bold text-foreground">
                        {city.to ? (
                          <Link to={city.to as string} className="transition-colors hover:text-primary">
                            {city.name}
                          </Link>
                        ) : (
                          city.name
                        )}
                      </h3>
                      <div className="mt-3 text-sm text-foreground/80">
                        <p>{city.address}</p>
                        <p className="mt-1">{PHONE}</p>
                      </div>
                      <a
                        href={mapUrl(city.address, city.name, state.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 self-start text-sm font-bold text-primary underline underline-offset-4 hover:no-underline"
                      >
                        View Map
                      </a>
                      <a
                        href={scheduleUrl}
                        className="mt-auto inline-block bg-primary px-5 py-3 pt-3 text-center text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90 mt-6"
                      >
                        Book a Consultation
                      </a>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background py-14 md:py-20">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <h2 className="text-2xl font-extrabold text-foreground md:text-3xl">
              <span className="font-extrabold">RESTORE Hair</span>{" "}
              <span className="font-light">Transplants</span>
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-foreground/80">
              <p>
                Explore the premier locations for hair restoration at RESTORE® Hair, conveniently
                spread across the country. Our expert facilities are dedicated to specializing in
                hair transplant, ensuring that each client receives exceptional care tailored to
                their unique needs. Whether you're looking to restore your hairline or seeking
                solutions to fill in thinning areas, our committed team of hair restoration
                surgeons are here to support you every step of the way. We understand that the
                hair transplant journey to reclaiming your confidence is deeply personal, and we
                are devoted to providing the highest quality of service and results which is why
                we have many hair transplant locations.
              </p>
              <p>
                At our clinics, we specialize in the RESTORE® No-Shave FUE™ hair transplant, a
                minimally invasive hair transplant technique designed for natural-looking results
                with minimal downtime. Our skilled medical professionals utilize cutting-edge
                tools and techniques to ensure precision, comfort, and lasting outcomes. Each hair
                transplant clinic is equipped with state-of-the-art technology and restoration
                surgeons to provide a seamless and personalized experience.
              </p>
              <p>
                To get started, we invite you to{" "}
                <a
                  href={scheduleUrl}
                  className="font-bold text-primary underline underline-offset-4 hover:no-underline"
                >
                  schedule your free consultation
                </a>{" "}
                today. Either from the comfort of your own home or at a location near you. During
                this consult at our clinic, you will receive a free info kit, as a gift, filled
                with hair transplant reviews and results. Let us help you embark on the
                transformative path to restoring your natural hair at RESTORE, where your
                satisfaction and renewed confidence are our top priorities.
              </p>
            </div>
          </div>
        </section>

        <RestoreLocationsMap hideList />

      </main>
      <Footer />
    </div>
  );
}
